import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { ownerApi } from '@/features/workspace/api';
import { useToast } from '@/hooks/useToast';
import { routes } from '@/lib/routes';

type AnnType = 'ANNOUNCEMENT' | 'POLL';
type AnnStatus = 'Опубликовано' | 'Запланирован' | 'Черновик' | 'Завершён' | 'Остановлен' | 'Архив';

type PollQuestion = {
  id: string;
  label: string;
  type: 'single' | 'multiple' | 'scale' | 'short' | 'long' | 'date' | 'file';
  required: boolean;
  options: string[];
};

type AnnItem = {
  id: string;
  type: AnnType;
  title: string;
  audience: string;
  language: string;
  status: AnnStatus;
  created_at: string;
  body: string;
  priority?: string;
  format?: string;
  mode?: string;
  questions?: PollQuestion[];
};

type ApiAnnouncement = {
  id: string;
  title: string;
  body: string;
  audience: string;
  created_at: string;
  kind?: string;
  language?: string;
  ui_status?: string;
  status?: string;
  priority?: string | null;
  format_label?: string | null;
  mode?: string | null;
  questions?: PollQuestion[];
};

const STATUS_FROM_API: Record<string, AnnStatus> = {
  DRAFT: 'Черновик',
  ACTIVE: 'Опубликовано',
  STOPPED: 'Остановлен',
  ARCHIVED: 'Архив',
};

function announcementFromApi(row: ApiAnnouncement): AnnItem {
  const ui = row.ui_status as AnnStatus | undefined;
  const mapped = row.status ? STATUS_FROM_API[row.status] : undefined;
  return {
    id: row.id,
    type: row.kind === 'POLL' ? 'POLL' : 'ANNOUNCEMENT',
    title: row.title,
    audience: row.audience,
    language: row.language || 'Русский',
    status: ui || mapped || 'Черновик',
    created_at: row.created_at,
    body: row.body,
    priority: row.priority || undefined,
    format: row.format_label || undefined,
    mode: row.mode || undefined,
    questions: row.questions,
  };
}

const emptyQuestion = (): PollQuestion => ({
  id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  label: '',
  type: 'single',
  required: false,
  options: ['Вариант 1', 'Вариант 2'],
});

export const OwnerAnnouncementsPage = () => {
  const navigate = useNavigate();
  const { notify } = useToast();
  const [annType, setAnnType] = useState<AnnType>('ANNOUNCEMENT');
  const [items, setItems] = useState<AnnItem[]>([]);
  const [filter, setFilter] = useState('ALL');
  const [questions, setQuestions] = useState<PollQuestion[]>([emptyQuestion()]);
  const [aiDraft, setAiDraft] = useState('');
  const [voiceListening, setVoiceListening] = useState(false);
  const voiceRecognitionRef = useRef<{ stop: () => void } | null>(null);
  const voiceAvailable =
    typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);
  const [form, setForm] = useState({
    title: '',
    audience: 'Все пользователи',
    language: 'Русский',
    priority: 'Обычный',
    mode: 'Добровольный · именной',
    reminder: 'Не отправлять',
    requiredMode: 'Добровольный',
    format: 'Карточка в ленте',
    start: '',
    end: '',
    body: '',
  });

  const filtered = useMemo(() => {
    if (filter === 'ALL') return items;
    if (filter === 'ANNOUNCEMENT' || filter === 'POLL') return items.filter((item) => item.type === filter);
    return items.filter((item) => item.status === filter);
  }, [items, filter]);

  useEffect(() => {
    document.body.classList.add('owner-uat-owner-route-open');
    return () => {
      document.body.classList.remove('owner-uat-owner-route-open');
    };
  }, []);

  const notifyRef = useRef(notify);
  notifyRef.current = notify;

  useEffect(() => {
    let cancelled = false;
    ownerApi
      .module('announcements')
      .then((payload) => {
        if (cancelled) return;
        const raw = Array.isArray(payload.items) ? (payload.items as ApiAnnouncement[]) : [];
        setItems(raw.map(announcementFromApi));
      })
      .catch(() => {
        if (!cancelled) notifyRef.current('Не удалось загрузить объявления');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const close = () => navigate(routes.owner);

  const publish = async (asDraft: boolean) => {
    if (!form.title.trim()) {
      notify('Укажите название');
      return;
    }
    const status: AnnStatus = asDraft
      ? 'Черновик'
      : form.start && new Date(form.start) > new Date()
        ? 'Запланирован'
        : 'Опубликовано';
    const item: AnnItem = {
      id: `${annType === 'POLL' ? 'POLL' : 'ANN'}-${String(items.length + 1).padStart(3, '0')}`,
      type: annType,
      title: form.title.trim(),
      audience: form.audience,
      language: form.language,
      status,
      created_at: new Date().toISOString(),
      body: form.body.trim() || 'Без дополнительного текста.',
      priority: annType === 'ANNOUNCEMENT' ? form.priority : undefined,
      format: annType === 'ANNOUNCEMENT' ? form.format : undefined,
      mode: annType === 'POLL' ? form.mode : undefined,
      questions: annType === 'POLL' ? questions : undefined,
    };
    if (!asDraft) {
      try {
        await ownerApi.createAnnouncement({
          title: item.title,
          body: item.body,
          audience: item.audience,
        });
      } catch {
        /* local */
      }
    }
    setItems((prev) => [item, ...prev]);
    setForm((prev) => ({ ...prev, title: '', body: '' }));
    notify(
      asDraft
        ? 'Черновик сохранён'
        : annType === 'POLL'
          ? 'Опрос опубликован'
          : 'Объявление опубликовано',
    );
  };

  const toggleVoiceInput = () => {
    type SpeRec = {
      lang: string;
      continuous: boolean;
      interimResults: boolean;
      onresult: ((ev: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
      onend: (() => void) | null;
      onerror: ((ev: { error?: string }) => void) | null;
      start: () => void;
      stop: () => void;
    };
    const SpeechRecognition =
      (window as unknown as { webkitSpeechRecognition?: new () => SpeRec }).webkitSpeechRecognition ||
      (window as unknown as { SpeechRecognition?: new () => SpeRec }).SpeechRecognition;
    if (!SpeechRecognition) return;
    if (voiceListening) {
      voiceRecognitionRef.current?.stop();
      setVoiceListening(false);
      return;
    }
    const rec = new SpeechRecognition();
    rec.lang = 'ru-RU';
    rec.continuous = false;
    rec.interimResults = false;
    rec.onresult = (ev) => {
      const said = ev.results?.[0]?.[0]?.transcript || '';
      if (said) setAiDraft((prev) => (prev ? `${prev} ${said}` : said));
    };
    rec.onend = () => setVoiceListening(false);
    rec.onerror = () => {
      setVoiceListening(false);
      notify('Не удалось распознать речь');
    };
    voiceRecognitionRef.current = rec;
    setVoiceListening(true);
    rec.start();
  };

  const applyAiDraft = () => {
    if (!aiDraft.trim()) {
      notify('Опишите задачу для черновика');
      return;
    }
    setForm((prev) => ({
      ...prev,
      title: prev.title || (annType === 'POLL' ? 'Опрос по теме' : 'Служебное объявление'),
      body: aiDraft.trim(),
    }));
    if (annType === 'POLL' && questions.every((q) => !q.label.trim())) {
      setQuestions([
        {
          ...emptyQuestion(),
          label: 'Насколько удобен кабинет для вашей роли?',
          type: 'scale',
          required: true,
          options: [],
        },
      ]);
    }
    notify('Черновик формы подготовлен — проверьте и отредактируйте');
  };

  return (
    <div
      className="owner-uat-owner-overlay open"
      role="dialog"
      aria-modal="true"
      aria-label="Объявления и опросы"
    >
      <div className="r59-route-shell">
        <header className="r59-route-top">
          <span className="owner-uat-route-icon" aria-hidden="true">
            ♛
          </span>
          <div>
            <div className="owner-uat-route-kicker">Владелец системы · Zertteu Academy</div>
            <h1>Объявления и опросы</h1>
            <p>Объявления, обновления и конструктор опросов в одном модуле</p>
          </div>
          <span className="spacer" />
          <button type="button" onClick={close}>
            ← Назад
          </button>
          <button type="button" onClick={close}>
            Закрыть ×
          </button>
        </header>

        <main className="r59-route-content">
      <section className="owner-uat-route-hero" style={{ '--owner-uat-accent': '#ff7b65' } as CSSProperties}>
        <div>
          <div className="owner-uat-route-kicker">Центр публикаций владельца</div>
          <h2>Объявления и опросы</h2>
          <p>
            Один общий контур: тип выбирается первым. Объявление и опрос используют общую историю,
            метаданные аудитории и языка и отдельные релевантные поля.
          </p>
        </div>
        <button
          type="button"
          className="owner-uat-btn primary"
          onClick={() =>
            setForm((prev) => ({
              ...prev,
              title: prev.title || (annType === 'POLL' ? 'Опрос по удобству кабинета' : 'Служебное объявление'),
              body:
                prev.body ||
                (annType === 'POLL'
                  ? 'Оцените разделы кабинета и предложите улучшения.'
                  : 'Краткое служебное сообщение для выбранной аудитории.'),
            }))
          }
        >
          Новый черновик
        </button>
      </section>

      <div className="owner-uat-kpis">
        <article className="owner-uat-kpi" style={{ '--kpi': '#41d2ff' } as CSSProperties}>
          <small>Всего записей</small>
          <strong>{items.length}</strong>
          <span>кабинет владельца</span>
        </article>
        <article className="owner-uat-kpi" style={{ '--kpi': '#49ddb7' } as CSSProperties}>
          <small>Активные</small>
          <strong>{items.filter((item) => item.status === 'Опубликовано').length}</strong>
          <span>опубликованы</span>
        </article>
        <article className="owner-uat-kpi" style={{ '--kpi': '#e2ad3a' } as CSSProperties}>
          <small>Запланированы</small>
          <strong>{items.filter((item) => item.status === 'Запланирован').length}</strong>
          <span>будущий показ</span>
        </article>
        <article className="owner-uat-kpi" style={{ '--kpi': '#9e80ff' } as CSSProperties}>
          <small>Черновики</small>
          <strong>{items.filter((item) => item.status === 'Черновик').length}</strong>
          <span>не опубликованы</span>
        </article>
      </div>

      <section className="owner-uat-panel">
        <header>
          <div>
            <h3>Создать / редактировать</h3>
            <p>Публикация выполняется только после отдельного подтверждения владельца.</p>
          </div>
          <span className="owner-uat-spacer" />
          <div className="owner-uat-switch">
            <button
              type="button"
              className={`owner-uat-switch-btn${annType === 'ANNOUNCEMENT' ? ' active' : ''}`}
              onClick={() => setAnnType('ANNOUNCEMENT')}
            >
              Объявление
            </button>
            <button
              type="button"
              className={`owner-uat-switch-btn${annType === 'POLL' ? ' active' : ''}`}
              onClick={() => setAnnType('POLL')}
            >
              Опрос
            </button>
          </div>
        </header>
        <div className="owner-uat-panel-body">
          <div className="owner-uat-grid2">
            <label style={{ display: 'grid', gap: 6 }}>
              <span style={{ color: '#8faebb', fontSize: 12 }}>Название</span>
              <input
                className="owner-uat-input"
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                placeholder={annType === 'POLL' ? 'Название опроса' : 'Название объявления'}
              />
            </label>
            <label style={{ display: 'grid', gap: 6 }}>
              <span style={{ color: '#8faebb', fontSize: 12 }}>Аудитория</span>
              <select
                className="owner-uat-select"
                value={form.audience}
                onChange={(e) => setForm((prev) => ({ ...prev, audience: e.target.value }))}
              >
                <option>Все пользователи</option>
                <option>Докторанты</option>
                <option>Обучающиеся под руководством</option>
                <option>Авторы / соавторы</option>
                <option>Руководители подразделений</option>
                <option>Выбрать группу…</option>
              </select>
            </label>
            <label style={{ display: 'grid', gap: 6 }}>
              <span style={{ color: '#8faebb', fontSize: 12 }}>Язык</span>
              <select
                className="owner-uat-select"
                value={form.language}
                onChange={(e) => setForm((prev) => ({ ...prev, language: e.target.value }))}
              >
                <option>Русский</option>
                <option>Қазақша</option>
                <option>Английский</option>
              </select>
            </label>
            {annType === 'ANNOUNCEMENT' ? (
              <>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ color: '#8faebb', fontSize: 12 }}>Приоритет</span>
                  <select
                    className="owner-uat-select"
                    value={form.priority}
                    onChange={(e) => setForm((prev) => ({ ...prev, priority: e.target.value }))}
                  >
                    <option>Обычный</option>
                    <option>Высокий</option>
                    <option>Критический</option>
                  </select>
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ color: '#8faebb', fontSize: 12 }}>Начало показа</span>
                  <input
                    className="owner-uat-input"
                    type="datetime-local"
                    value={form.start}
                    onChange={(e) => setForm((prev) => ({ ...prev, start: e.target.value }))}
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ color: '#8faebb', fontSize: 12 }}>Окончание показа</span>
                  <input
                    className="owner-uat-input"
                    type="datetime-local"
                    value={form.end}
                    onChange={(e) => setForm((prev) => ({ ...prev, end: e.target.value }))}
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ color: '#8faebb', fontSize: 12 }}>Формат показа</span>
                  <select
                    className="owner-uat-select"
                    value={form.format}
                    onChange={(e) => setForm((prev) => ({ ...prev, format: e.target.value }))}
                  >
                    <option>Верхний баннер</option>
                    <option>Центральное модальное окно</option>
                    <option>Карточка в ленте</option>
                    <option>Уведомление</option>
                    <option>Блок на главной</option>
                  </select>
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ color: '#8faebb', fontSize: 12 }}>Фото / афиша / логотип</span>
                  <input className="owner-uat-input" type="file" accept="image/*" />
                </label>
              </>
            ) : (
              <>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ color: '#8faebb', fontSize: 12 }}>Срок опроса</span>
                  <input
                    className="owner-uat-input"
                    type="datetime-local"
                    value={form.end}
                    onChange={(e) => setForm((prev) => ({ ...prev, end: e.target.value }))}
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ color: '#8faebb', fontSize: 12 }}>Режим</span>
                  <select
                    className="owner-uat-select"
                    value={form.mode}
                    onChange={(e) => setForm((prev) => ({ ...prev, mode: e.target.value }))}
                  >
                    <option>Обязательный · именной</option>
                    <option>Добровольный · именной</option>
                    <option>Добровольный · анонимный</option>
                  </select>
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ color: '#8faebb', fontSize: 12 }}>Напоминание</span>
                  <select
                    className="owner-uat-select"
                    value={form.reminder}
                    onChange={(e) => setForm((prev) => ({ ...prev, reminder: e.target.value }))}
                  >
                    <option>Не отправлять</option>
                    <option>За 24 часа</option>
                    <option>За 3 дня</option>
                    <option>Каждые 3 дня до срока</option>
                  </select>
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ color: '#8faebb', fontSize: 12 }}>Обязательность</span>
                  <select
                    className="owner-uat-select"
                    value={form.requiredMode}
                    onChange={(e) => setForm((prev) => ({ ...prev, requiredMode: e.target.value }))}
                  >
                    <option>Добровольный</option>
                    <option>Обязательный</option>
                  </select>
                </label>
              </>
            )}
          </div>
          <label style={{ display: 'grid', gap: 6, marginTop: 10 }}>
            <span style={{ color: '#8faebb', fontSize: 12 }}>
              {annType === 'POLL' ? 'Цель / описание' : 'Текст / описание'}
            </span>
            <textarea
              className="owner-uat-textarea"
              value={form.body}
              onChange={(e) => setForm((prev) => ({ ...prev, body: e.target.value }))}
            />
          </label>

          {annType === 'POLL' ? (
            <section className="owner-uat-panel" style={{ marginTop: 12 }}>
              <header>
                <div>
                  <h3>Конструктор вопросов</h3>
                  <p>Один / несколько / шкала / короткий и длинный текст / дата / файл</p>
                </div>
                <span className="owner-uat-spacer" />
                <button
                  type="button"
                  className="owner-uat-btn"
                  onClick={() => setQuestions((prev) => [...prev, emptyQuestion()])}
                >
                  + Вопрос
                </button>
              </header>
              <div className="owner-uat-panel-body" style={{ display: 'grid', gap: 10 }}>
                {questions.map((q, index) => (
                  <article key={q.id} className="owner-uat-card" style={{ '--kpi': '#9e80ff' } as CSSProperties}>
                    <div className="owner-uat-actions" style={{ marginBottom: 8, flexWrap: 'wrap' }}>
                      <span className="owner-uat-chip" style={{ '--chip': '#41d2ff' } as CSSProperties}>
                        {index + 1}
                      </span>
                      <input
                        className="owner-uat-input"
                        style={{ flex: 1, minWidth: 180 }}
                        value={q.label}
                        onChange={(e) =>
                          setQuestions((prev) =>
                            prev.map((item) =>
                              item.id === q.id ? { ...item, label: e.target.value } : item,
                            ),
                          )
                        }
                        placeholder="Текст вопроса"
                      />
                      <select
                        className="owner-uat-select"
                        value={q.type}
                        onChange={(e) =>
                          setQuestions((prev) =>
                            prev.map((item) =>
                              item.id === q.id
                                ? { ...item, type: e.target.value as PollQuestion['type'] }
                                : item,
                            ),
                          )
                        }
                      >
                        <option value="single">Один вариант</option>
                        <option value="multiple">Несколько вариантов</option>
                        <option value="scale">Шкала</option>
                        <option value="short">Короткий текст</option>
                        <option value="long">Длинный текст</option>
                        <option value="date">Дата</option>
                        <option value="file">Файл</option>
                      </select>
                      <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
                        <input
                          type="checkbox"
                          checked={q.required}
                          onChange={(e) =>
                            setQuestions((prev) =>
                              prev.map((item) =>
                                item.id === q.id ? { ...item, required: e.target.checked } : item,
                              ),
                            )
                          }
                        />
                        Обязательный
                      </label>
                      <button
                        type="button"
                        className="owner-uat-btn"
                        onClick={() => setQuestions((prev) => prev.filter((item) => item.id !== q.id))}
                      >
                        ×
                      </button>
                    </div>
                    {q.type === 'single' || q.type === 'multiple' ? (
                      <div style={{ display: 'grid', gap: 6 }}>
                        {q.options.map((opt, optIndex) => (
                          <div key={`${q.id}-${optIndex}`} className="owner-uat-actions">
                            <input
                              className="owner-uat-input"
                              style={{ flex: 1 }}
                              value={opt}
                              onChange={(e) =>
                                setQuestions((prev) =>
                                  prev.map((item) => {
                                    if (item.id !== q.id) return item;
                                    const options = [...item.options];
                                    options[optIndex] = e.target.value;
                                    return { ...item, options };
                                  }),
                                )
                              }
                            />
                            <button
                              type="button"
                              className="owner-uat-btn"
                              onClick={() =>
                                setQuestions((prev) =>
                                  prev.map((item) =>
                                    item.id === q.id
                                      ? {
                                          ...item,
                                          options: item.options.filter((_, i) => i !== optIndex),
                                        }
                                      : item,
                                  ),
                                )
                              }
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="owner-uat-btn"
                          onClick={() =>
                            setQuestions((prev) =>
                              prev.map((item) =>
                                item.id === q.id
                                  ? { ...item, options: [...item.options, `Вариант ${item.options.length + 1}`] }
                                  : item,
                              ),
                            )
                          }
                        >
                          + вариант
                        </button>
                      </div>
                    ) : null}
                  </article>
                ))}
                {!questions.length ? <div className="owner-uat-empty">Добавьте первый вопрос.</div> : null}
              </div>
            </section>
          ) : null}

          <div
            className="owner-uat-card"
            style={{ marginTop: 12, '--kpi': '#9e80ff', display: 'grid', gap: 8 } as CSSProperties}
          >
            <strong>Черновик формы от консультанта</strong>
            <span style={{ color: '#8faebb', fontSize: 11 }}>
              Помощник только для этой формы, не общий консультант владельца.
            </span>
            <textarea
              className="owner-uat-textarea"
              value={aiDraft}
              onChange={(e) => setAiDraft(e.target.value)}
              placeholder="Напишите или продиктуйте: «Создай опрос для докторантов о…»"
            />
            <div className="owner-uat-actions" style={{ justifyContent: 'flex-start' }}>
              {voiceAvailable ? (
                <button
                  type="button"
                  className="owner-uat-btn"
                  onClick={toggleVoiceInput}
                  aria-pressed={voiceListening}
                >
                  {voiceListening ? 'Стоп' : 'Голос'}
                </button>
              ) : null}
              <button type="button" className="owner-uat-btn" onClick={applyAiDraft}>
                Подготовить черновик
              </button>
            </div>
          </div>

          <div className="owner-uat-actions" style={{ marginTop: 14, justifyContent: 'flex-start' }}>
            <button type="button" className="owner-uat-btn" onClick={() => void publish(true)}>
              Сохранить черновик
            </button>
            <button
              type="button"
              className="owner-uat-btn"
              onClick={() =>
                notify(
                  form.title.trim()
                    ? `Предпросмотр: «${form.title.trim()}» · ${form.audience}`
                    : 'Заполните название для предпросмотра',
                )
              }
            >
              Точный предпросмотр
            </button>
            <button type="button" className="owner-uat-btn primary" onClick={() => void publish(false)}>
              Опубликовать
            </button>
          </div>
        </div>
      </section>

      <section className="owner-uat-panel">
        <header>
          <div>
            <h3>История и статистика</h3>
            <p>Все · объявления · опросы · черновики · запланированы · активные · завершены · остановлены · архив</p>
          </div>
          <span className="owner-uat-spacer" />
          <select className="owner-uat-select" value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="ALL">Все</option>
            <option value="ANNOUNCEMENT">Объявления</option>
            <option value="POLL">Опросы</option>
            <option value="Черновик">Черновики</option>
            <option value="Опубликовано">Активные</option>
            <option value="Запланирован">Запланированы</option>
            <option value="Завершён">Завершены</option>
            <option value="Остановлен">Остановлены</option>
            <option value="Архив">Архив</option>
          </select>
        </header>
        <div className="owner-uat-panel-body">
          <div className="owner-uat-table-wrap">
            <table className="owner-uat-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Тип / название</th>
                  <th>Аудитория</th>
                  <th>Статус</th>
                  <th>Статистика</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={item.id} data-open="annitem">
                    <td>
                      <code>{item.id}</code>
                    </td>
                    <td>
                      <b>{item.type === 'POLL' ? 'Опрос' : 'Объявление'}</b>
                      <br />
                      {item.title}
                      <br />
                      <small style={{ color: '#7f9eac' }}>{item.body}</small>
                    </td>
                    <td>
                      {item.audience}
                      <br />
                      <small style={{ color: '#7f9eac' }}>{item.language}</small>
                    </td>
                    <td>
                      <span
                        className={`owner-uat-status ${
                          item.status === 'Опубликовано'
                            ? 'ready'
                            : item.status === 'Запланирован'
                              ? 'planned'
                              : item.status === 'Черновик'
                                ? 'draft'
                                : 'info'
                        }`}
                      >
                        {item.status}
                      </span>
                    </td>
                    <td>
                      {item.type === 'POLL'
                        ? `Вопросов: ${item.questions?.length ?? 0}`
                        : `Формат: ${item.format || '—'}`}
                      <br />
                      <small style={{ color: '#7f9eac' }}>
                        {new Date(item.created_at).toLocaleString('ru-RU')}
                      </small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!filtered.length ? <div className="owner-uat-empty">Записей по фильтру нет.</div> : null}
        </div>
      </section>
        </main>
      </div>
    </div>
  );
};
